import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Stack, CircularProgress, Alert, Snackbar,
  Fade, useMediaQuery, TextField, Autocomplete, Chip, IconButton, Divider,
  Card, CardContent, InputAdornment, MenuItem
} from '@mui/material';
import {
  ShoppingCart, Add, Delete, PhotoCamera, Receipt, PointOfSale,
  NavigateBefore, NavigateNext, AttachMoney
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getMySupplierBarang, createPembelian } from '../API/supplierPenyedia';
import { getBantuan } from '../API/bantuan';

const formatRp = (n) => (!n && n !== 0) ? '-' : 'Rp ' + Number(n).toLocaleString('id-ID');

// ============================================================
// FUNGSI FORMAT RUPIAH UNTUK INPUT
// ============================================================
const formatRupiahInput = (value) => {
  if (!value) return '';
  const number = value.replace(/[^0-9]/g, '');
  if (!number) return '';
  return number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseRupiahInput = (value) => {
  if (!value) return '';
  return value.replace(/[^0-9]/g, '');
};

const InputPembelian = () => {
  const userRole = useSelector((state) => state.authAdmin?.user?.role);
  const isAdmin = userRole === 'admin';
  const isSupplier = userRole === 'Supplier Penyedia';
  const isMobile = useMediaQuery('(max-width:800px)');

  const [supplierList, setSupplierList] = useState([]);
  const [bantuanOpts, setBantuanOpts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selSupplier, setSelSupplier] = useState(null);
  const [selBantuan, setSelBantuan] = useState(null);
  const [autoSupplierNotice, setAutoSupplierNotice] = useState('');
  const [kategoriPembelian, setKategoriPembelian] = useState('Pembelian Tahap 1');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Cart items - tambahkan field untuk display format
  const [items, setItems] = useState([{ 
    namaBarang: '', 
    hargaSatuan: '', 
    hargaSatuanDisplay: '', // Untuk tampilan format Rupiah
    jumlahBarang: '',
    satuan: '',
    kategoriPembelian: 'Pembelian Tahap 1'
  }]);
  const [fotoKwitansi, setFotoKwitansi] = useState(null);
  const [fotoBarang, setFotoBarang] = useState(null);
  const [saving, setSaving] = useState(false);

  // History pembelian supplier terpilih
  const [history, setHistory] = useState([]);

  useEffect(() => {
    setItems((prev) => prev.map((item) => ({ ...item, kategoriPembelian })));
  }, [kategoriPembelian]);

  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchSupplier = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMySupplierBarang({ limit: 100 });
      const list = (res.data.data || []).map((s) => ({
        id: s.id,
        label: `${s.id} - ${s.namaSupplier} (${s.alamatSupplier || '-'})`,
        namaSupplier: s.namaSupplier,
        alamatSupplier: s.alamatSupplier,
        nomorWA: s.nomorWA,
        Pembelian: s.Pembelian || [],
      }));
      setSupplierList(list);
      if (isSupplier && list.length === 1) setSelSupplier(list[0]);
    } catch (err) {
      console.error(err);
      notify('Gagal memuat data supplier', 'error');
    } finally {
      setLoading(false);
    }
  }, [isSupplier]);

  const fetchBantuanOpts = useCallback(async () => {
    try {
      const res = await getBantuan({ page: 1, limit: 200 });
      const list = (res.data.data || []).map((b) => {
        let supplierData = null;
        let supplierBarang = [];
        
        if (b.SupplierAdmin) {
          supplierData = {
            id: b.SupplierAdmin.id,
            username: b.SupplierAdmin.username,
            email: b.SupplierAdmin.email,
            role: b.SupplierAdmin.role
          };
        }
        
        if (b.SupplierBarang && b.SupplierBarang.length > 0) {
          supplierBarang = b.SupplierBarang;
        } else if (b.supplierBarang && b.supplierBarang.length > 0) {
          supplierBarang = b.supplierBarang;
        } else if (b.pemohon?.supplierBarang && b.pemohon.supplierBarang.length > 0) {
          supplierBarang = b.pemohon.supplierBarang;
        } else if (b.suppliers && b.suppliers.length > 0) {
          supplierBarang = b.suppliers;
        }
        
        let matchedSupplier = null;
        if (b.supplierId && supplierBarang.length > 0) {
          const found = supplierBarang.find(s => s.adminId === b.supplierId);
          if (found) {
            matchedSupplier = {
              id: found.id,
              label: `${found.id} - ${found.namaSupplier} (${found.alamatSupplier || '-'})`,
              namaSupplier: found.namaSupplier,
              alamatSupplier: found.alamatSupplier,
              nomorWA: found.nomorWA,
              Pembelian: found.Pembelian || [],
              adminId: found.adminId
            };
          }
        }
        
        return {
          id: b.id,
          label: `#${b.id} - ${b.pemohon?.nama || b.namaLengkap || '-'} (${b.JenisBantuan || '-'})`,
          bantuan: b,
          supplierData: supplierData,
          supplierBarang: supplierBarang,
          matchedSupplier: matchedSupplier
        };
      });
      setBantuanOpts(list);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchSupplier(); fetchBantuanOpts(); }, [fetchSupplier, fetchBantuanOpts]);

  useEffect(() => {
    setHistory(selSupplier?.Pembelian || []);
  }, [selSupplier]);

  useEffect(() => {
    if (!selBantuan) {
      setSelSupplier(null);
      setAutoSupplierNotice('');
      return;
    }

    const resolveSupplier = () => {
      if (selBantuan.matchedSupplier) {
        const fullSupplier = supplierList.find((s) => s.id === selBantuan.matchedSupplier.id);
        return fullSupplier || selBantuan.matchedSupplier;
      }

      if (selBantuan.supplierBarang && selBantuan.supplierBarang.length > 0) {
        const firstSupplier = selBantuan.supplierBarang[0];
        const supplierObj = {
          id: firstSupplier.id,
          label: `${firstSupplier.id} - ${firstSupplier.namaSupplier} (${firstSupplier.alamatSupplier || '-'})`,
          namaSupplier: firstSupplier.namaSupplier,
          alamatSupplier: firstSupplier.alamatSupplier,
          nomorWA: firstSupplier.nomorWA,
          Pembelian: firstSupplier.Pembelian || [],
          adminId: firstSupplier.adminId,
        };
        return supplierList.find((s) => s.id === firstSupplier.id) || supplierObj;
      }

      if (selBantuan.supplierData) {
        const supplierBarang = selBantuan.supplierBarang || [];
        const found = supplierBarang.find((s) => s.adminId === selBantuan.supplierData.id);
        if (found) {
          const supplierObj = {
            id: found.id,
            label: `${found.id} - ${found.namaSupplier} (${found.alamatSupplier || '-'})`,
            namaSupplier: found.namaSupplier,
            alamatSupplier: found.alamatSupplier,
            nomorWA: found.nomorWA,
            Pembelian: found.Pembelian || [],
            adminId: found.adminId,
          };
          return supplierList.find((s) => s.id === found.id) || supplierObj;
        }
      }

      return null;
    };

    const nextSupplier = resolveSupplier();
    setSelSupplier((current) => {
      const sameSupplier = current?.id === nextSupplier?.id;
      if (sameSupplier) {
        return current;
      }
      if (nextSupplier) {
        setAutoSupplierNotice(`Supplier otomatis dipilih: ${nextSupplier.namaSupplier}`);
        return nextSupplier;
      }
      setAutoSupplierNotice('Bantuan ini belum memiliki supplier terdaftar');
      return null;
    });
  }, [selBantuan, supplierList]);

  // ============================================================
  // HANDLER ITEM DENGAN FORMAT RUPIAH
  // ============================================================

  const handleItem = (i, field, val) => {
  setItems((p) => {
    const arr = [...p];
    
    if (field === 'hargaSatuan') {
      const rawValue = val.replace(/[^0-9]/g, '');
      
      arr[i] = { 
        ...arr[i], 
        hargaSatuan: rawValue,
        hargaSatuanDisplay: rawValue ? formatRupiahInput(rawValue) : ''
      };
    } else {
      arr[i] = { ...arr[i], [field]: val };
    }
    return arr;
  });
};

// ============================================================
// HANDLER FOCUS - TIDAK PERLU DIUBAH
// ============================================================
const handleHargaFocus = (i) => {
  // Saat focus, tampilkan angka tanpa format
  setItems((p) => {
    const arr = [...p];
    arr[i] = { 
      ...arr[i], 
      hargaSatuanDisplay: arr[i].hargaSatuan || ''
    };
    return arr;
  });
};

const handleHargaBlur = (i) => {
  // Saat blur, format dengan Rupiah
  setItems((p) => {
    const arr = [...p];
    const value = arr[i].hargaSatuan;
    if (value) {
      arr[i].hargaSatuanDisplay = formatRupiahInput(value);
    } else {
      arr[i].hargaSatuanDisplay = '';
    }
    return arr;
  });
};

  const addItem = () => setItems((p) => [...p, { 
    namaBarang: '', 
    hargaSatuan: '', 
    hargaSatuanDisplay: '', 
    jumlahBarang: '',
    satuan: '',
    kategoriPembelian: 'Pembelian Tahap 1'
  }]);
  
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const totalTransaksi = items.reduce((sum, it) => sum + ((Number(it.hargaSatuan) || 0) * (Number(it.jumlahBarang) || 0)), 0);

  const handleSubmit = async () => {
    if (!selSupplier) return notify('Pilih supplier dulu', 'warning');
    if (!selBantuan?.id) return notify('Pilih penerima bantuan untuk transaksi ini', 'warning');
    const validItems = items.filter((it) => it.namaBarang.trim());
    if (validItems.length === 0) return notify('Minimal 1 item barang', 'warning');
    if (validItems.some((it) => !it.satuan.trim() || Number(it.hargaSatuan) <= 0 || Number(it.jumlahBarang) <= 0)) return notify('Nama, harga, jumlah, dan satuan setiap item wajib diisi', 'warning');
    // Foto kwitansi & foto barang opsional — dilengkapi supplier saat proses pengiriman.

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('SupplierBarangId', selSupplier.id);
      fd.append('BantuanId', selBantuan.id);
      fd.append('detailTransaksi', JSON.stringify(validItems.map((it) => ({
        namaBarang: it.namaBarang.trim(),
        hargaSatuan: Number(it.hargaSatuan) || 0,
        jumlahBarang: Number(it.jumlahBarang) || 0,
        satuan: it.satuan.trim(),
        kategoriPembelian: it.kategoriPembelian || kategoriPembelian,
      }))));
      if (fotoKwitansi) fd.append('fotoKwitansi', fotoKwitansi);
      if (fotoBarang) fd.append('fotoBarang', fotoBarang);
      await createPembelian(fd);
      notify('Pembelian berhasil disimpan');
      setItems([{ namaBarang: '', hargaSatuan: '', hargaSatuanDisplay: '', jumlahBarang: '', satuan: '', kategoriPembelian }]);
      setFotoKwitansi(null);
      setFotoBarang(null);
      
      const res = await getMySupplierBarang({ SupplierBarangId: selSupplier.id });
      const updated = (res.data.data || []).map((s) => ({
        id: s.id,
        label: `${s.id} - ${s.namaSupplier}`,
        namaSupplier: s.namaSupplier,
        alamatSupplier: s.alamatSupplier,
        Pembelian: s.Pembelian || [],
      }));
      setSupplierList(updated);
      const fresh = updated.find((s) => s.id === selSupplier.id);
      if (fresh) setSelSupplier(fresh);
    } catch (err) {
      console.error(err);
      notify(err.response?.data?.message || 'Gagal menyimpan pembelian', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBantuanChange = (event, value) => {
    setSelBantuan(value);
    if (!value) {
      setSelSupplier(null);
      setAutoSupplierNotice('');
      return;
    }

    if (value.matchedSupplier) {
      const fullSupplier = supplierList.find((s) => s.id === value.matchedSupplier.id);
      const selectedSupplier = fullSupplier || value.matchedSupplier;
      setSelSupplier(selectedSupplier);
      setAutoSupplierNotice(`Supplier otomatis dipilih: ${selectedSupplier.namaSupplier}`);
      notify(`Supplier otomatis dipilih: ${selectedSupplier.namaSupplier}`, 'info');
      return;
    }

    if (value.supplierBarang && value.supplierBarang.length > 0) {
      const firstSupplier = value.supplierBarang[0];
      const supplierObj = {
        id: firstSupplier.id,
        label: `${firstSupplier.id} - ${firstSupplier.namaSupplier} (${firstSupplier.alamatSupplier || '-'})`,
        namaSupplier: firstSupplier.namaSupplier,
        alamatSupplier: firstSupplier.alamatSupplier,
        nomorWA: firstSupplier.nomorWA,
        Pembelian: firstSupplier.Pembelian || [],
        adminId: firstSupplier.adminId,
      };

      const fullSupplier = supplierList.find((s) => s.id === firstSupplier.id);
      const selectedSupplier = fullSupplier || supplierObj;
      setSelSupplier(selectedSupplier);
      setAutoSupplierNotice(`Supplier otomatis dipilih: ${selectedSupplier.namaSupplier}`);
      notify(`Supplier otomatis dipilih: ${selectedSupplier.namaSupplier}`, 'info');
      return;
    }

    if (value.supplierData) {
      const supplierBarang = value.supplierBarang || [];
      const found = supplierBarang.find((s) => s.adminId === value.supplierData.id);
      if (found) {
        const supplierObj = {
          id: found.id,
          label: `${found.id} - ${found.namaSupplier} (${found.alamatSupplier || '-'})`,
          namaSupplier: found.namaSupplier,
          alamatSupplier: found.alamatSupplier,
          nomorWA: found.nomorWA,
          Pembelian: found.Pembelian || [],
          adminId: found.adminId,
        };
        const fullSupplier = supplierList.find((s) => s.id === found.id);
        const selectedSupplier = fullSupplier || supplierObj;
        setSelSupplier(selectedSupplier);
        setAutoSupplierNotice(`Supplier otomatis dipilih: ${selectedSupplier.namaSupplier}`);
        notify(`Supplier otomatis dipilih: ${selectedSupplier.namaSupplier}`, 'info');
        return;
      }
    }

    setSelSupplier(null);
    setAutoSupplierNotice('Bantuan ini belum memiliki supplier terdaftar');
    notify('Bantuan ini belum memiliki supplier terdaftar', 'warning');
  };

  return (
    <Box sx={{
         minHeight: '100vh',
         ml: { xs: 0, md: isMobile ? 0 : '250px' },
        
       }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <PointOfSale color="primary" />
            <Typography variant="h5" fontWeight={700}>Input Pembelian (POS)</Typography>
          </Box>

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : supplierList.length === 0 ? (
            <Alert severity="info">
              {isSupplier
                ? 'Anda belum terdaftar sebagai supplier. Hubungi admin untuk membuat akun supplier Anda.'
                : 'Belum ada data supplier. Tambah supplier di halaman Data Supplier.'}
            </Alert>
          ) : (
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={{ xs: 2, lg: 3 }} sx={{ minWidth: 0 }}>
              {/* LEFT: Form POS */}
              <Box sx={{ flex: 2, minWidth: 0 }}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Supplier</Typography>
                    
                    <Autocomplete
                      options={bantuanOpts}
                      value={selBantuan}
                      getOptionLabel={(o) => o?.label ?? ''}
                      isOptionEqualToValue={(o, v) => o?.id === v?.id}
                      onChange={handleBantuanChange}
                      renderInput={(params) => (
                        <TextField {...params} size="small" label="Penerima Bantuan" required />
                      )}
                      sx={{ mb: 2 }}
                    />
                    
                    <Autocomplete
                      options={supplierList}
                      value={selSupplier}
                      getOptionLabel={(o) => o?.label ?? ''}
                      isOptionEqualToValue={(o, v) => o?.id === v?.id}
                      onChange={(e, val) => {
                        setSelSupplier(val);
                        if (val) {
                          setHistory(val.Pembelian || []);
                        }
                      }}
                      disabled={isSupplier && supplierList.length === 1}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          size="small" 
                          label="Pilih Supplier" 
                          helperText={selBantuan?.matchedSupplier ? "Supplier otomatis dari bantuan" : "Pilih supplier manual"}
                        />
                      )}
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      select
                      label="Kategori Pembelian"
                      size="small"
                      value={kategoriPembelian}
                      onChange={(e) => setKategoriPembelian(e.target.value)}
                      fullWidth
                      sx={{ mb: 2, maxWidth: { sm: 320 } }}
                    >
                      <MenuItem value="Pembelian Tahap 1">Pembelian Tahap 1</MenuItem>
                      <MenuItem value="Pembelian Tahap 2">Pembelian Tahap 2</MenuItem>
                    </TextField>

                    {selSupplier && (
                      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Chip 
                          size="small" 
                          label={selSupplier.namaSupplier} 
                          color={selBantuan?.matchedSupplier ? "success" : "primary"} 
                        />
                        <Chip size="small" label={`WA: ${selSupplier.nomorWA || '-'}`} variant="outlined" />
                        {(selBantuan?.matchedSupplier || autoSupplierNotice) && (
                          <Chip size="small" label={autoSupplierNotice || '✅ Auto-select dari bantuan'} color="success" variant="outlined" />
                        )}
                      </Stack>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Cart items */}
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      <ShoppingCart fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Item Pembelian
                    </Typography>
                    
                    {items.map((it, i) => (
                      <Box
                        key={i}
                        sx={{
                          mb: 1.5,
                          bgcolor: '#f8f9fa',
                          p: 1.5,
                          borderRadius: 1,
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'minmax(180px, 1.8fr) minmax(90px, 0.8fr) minmax(130px, 1.2fr) minmax(100px, 0.9fr) auto auto',
                          },
                          gap: 1,
                          alignItems: 'center',
                          minWidth: 0,
                        }}
                      >
                        <TextField 
                          label="Nama Barang" 
                          size="small" 
                          value={it.namaBarang}
                          onChange={(e) => handleItem(i, 'namaBarang', e.target.value)} 
                          fullWidth
                        />
                        
                        {/* ============================================================
                            INPUT HARGA DENGAN FORMAT RUPIAH
                            ============================================================ */}
                        <TextField 
                          label="Jumlah" 
                          size="small" 
                          type="number" 
                          value={it.jumlahBarang}
                          onChange={(e) => handleItem(i, 'jumlahBarang', e.target.value)} 
                          fullWidth
                          inputProps={{ min: 1 }}
                          sx={{
                            '& input[type=number]': { MozAppearance: 'textfield' },
                            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0
                            }
                          }} 
                        />
                        <TextField 
                          label="Harga Satuan" 
                          size="small" 
                          value={it.hargaSatuanDisplay || it.hargaSatuan || ''}
                          onChange={(e) => handleItem(i, 'hargaSatuan', e.target.value)}
                          onFocus={() => handleHargaFocus(i)}
                          onBlur={() => handleHargaBlur(i)}
                          fullWidth
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">Rp</InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          label="Satuan"
                          size="small"
                          required
                          value={it.satuan}
                          onChange={(e) => handleItem(i, 'satuan', e.target.value)}
                          inputProps={{ maxLength: 50 }}
                          placeholder="sak, batang, unit"
                          fullWidth
                        />
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600,
                            color: 'primary.main',
                            whiteSpace: 'nowrap',
                            textAlign: { xs: 'left', sm: 'right' }
                          }}
                        >
                          {formatRp((Number(it.hargaSatuan) || 0) * (Number(it.jumlahBarang) || 0))}
                        </Typography>
                        
                        {/* Tombol hapus diperkecil */}
                        <IconButton 
                          size="small" 
                          color="error" 
                          disabled={items.length === 1} 
                          onClick={() => removeItem(i)}
                          sx={{ 
                            width: 26,
                            height: 26,
                            p: 0.25,
                            border: '1px solid',
                            borderColor: 'error.light',
                            borderRadius: 1,
                            '& .MuiSvgIcon-root': { fontSize: 16 },
                            justifySelf: { xs: 'end', sm: 'center' },
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    ))}
                    
                    <Button size="small" startIcon={<Add />} onClick={addItem} sx={{ mb: 2 }}>
                      Tambah Item
                    </Button>

                    <Divider sx={{ my: 1 }} />

                    {/* Total */}
                    <Stack 
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between" 
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      sx={{ 
                        mb: 2,
                        p: 2,
                        bgcolor: '#e3f2fd',
                        borderRadius: 2
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>Total Transaksi</Typography>
                      <Typography variant="h6" fontWeight={700} color="primary">
                        {formatRp(totalTransaksi)}
                      </Typography>
                    </Stack>

                    {/* Upload foto — opsional */}
                    {/* <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Foto kwitansi & foto barang bersifat opsional. Supplier melengkapi saat proses pengiriman.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                      <Button 
                        variant="outlined" 
                        component="label" 
                        startIcon={<PhotoCamera />} 
                        fullWidth
                        sx={{ 
                          borderStyle: 'dashed',
                          '&:hover': { borderStyle: 'dashed' }
                        }}
                      >
                        Foto Kwitansi {fotoKwitansi ? '✓' : '(opsional)'}
                        <input type="file" accept="image/*" hidden onChange={(e) => setFotoKwitansi(e.target.files?.[0] || null)} />
                      </Button>
                      <Button 
                        variant="outlined" 
                        component="label" 
                        startIcon={<PhotoCamera />} 
                        fullWidth
                        sx={{ 
                          borderStyle: 'dashed',
                          '&:hover': { borderStyle: 'dashed' }
                        }}
                      >
                        Foto Barang {fotoBarang ? '✓' : '(opsional)'}
                        <input type="file" accept="image/*" hidden onChange={(e) => setFotoBarang(e.target.files?.[0] || null)} />
                      </Button>
                    </Stack> */}

                    <Button 
                      variant="contained" 
                      color="success" 
                      size="large" 
                      fullWidth
                      disabled={saving || !selSupplier || !selBantuan}
                      onClick={handleSubmit} 
                      startIcon={<Receipt />}
                      sx={{ borderRadius: 2 }}
                    >
                      {saving ? 'Menyimpan...' : 'Simpan Pembelian'}
                    </Button>
                  </CardContent>
                </Card>
              </Box>

              {/* RIGHT: History pembelian supplier */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Card elevation={2} sx={{ position: { xs: 'static', lg: 'sticky' }, top: { lg: 16 } }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      <Receipt fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Riwayat Pembelian
                    </Typography>
                    {selSupplier ? (
                      history.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                          Belum ada pembelian.
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {history.map((p) => (
                            <Box key={p.id} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                  #{p.id} · {new Date(p.createdAt).toLocaleDateString('id-ID')}
                                  {p.BantuanId ? ` · Bantuan #${p.BantuanId}` : ''}
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={700} color="primary">
                                  {formatRp(p.totalTransaksi)}
                                </Typography>
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                {p.DetailTransaksi?.length || 0} item
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        Pilih supplier untuk lihat riwayat.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Stack>
          )}

          <Snackbar 
            open={snackbar.open} 
            autoHideDuration={4000}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            TransitionComponent={Fade} 
             anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
          >
            <Alert 
              onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
              severity={snackbar.severity} 
              variant="filled" 
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
    </Box>
  );
};

export default InputPembelian;
